import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faPenToSquare, faTrash, faXmark, faArrowLeft, faCheckCircle, faTimesCircle, faPlus, faTag } from '@fortawesome/free-solid-svg-icons';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import useAutoDismissSnack from '../../../Hooks/AutoDismissSnack';

const BASE = `${BASE_URL}`;

function ControlParkings() {
  const { id_torre } = useParams();
  const navigate = useNavigate();

  const [parkings, setParkings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, ocupados: 0, disponibles: 0 });

  // Variaciones
  const [variaciones, setVariaciones] = useState([]);
  const [varOpen, setVarOpen] = useState(false);
  const [varName, setVarName] = useState('');
  const [varEditOpen, setVarEditOpen] = useState(false);
  const [varEditItem, setVarEditItem] = useState(null);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });
  const closeConfirm = () => setConfirmDialog({ open: false, message: '', onConfirm: null });

  // Add parking form
  const [open, setOpen] = useState(false);
  const [numParqueadero, setNumParqueadero] = useState('');
  const [addVariacionId, setAddVariacionId] = useState('');

  // Edit parking form
  const [editOpen, setEditOpen] = useState(false);
  const [selectedParking, setSelectedParking] = useState(null);

  // Snackbar
  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState('');
  const [snackMessage, setSnackMessage] = useState('');
  useAutoDismissSnack(openSnack, setOpenSnack);

  useEffect(() => {
    window.scrollTo(0, 0);
    getParkings();
    getStats();
    fetchVariaciones();
  }, [id_torre]);

  const snack = (status, msg) => { setSnackStatus(status); setSnackMessage(msg); setOpenSnack(true); };

  const getParkings = () => {
    axios.get(`${BASE}/parkings/tower/${id_torre}`)
      .then(r => setParkings(r.data))
      .catch(e => console.error(e));
  };

  const getStats = () => {
    axios.get(`${BASE}/parkingStats/${id_torre}`)
      .then(r => setStats(r.data))
      .catch(e => console.error(e));
  };

  const fetchVariaciones = () => {
    axios.get(`${BASE}/variaciones/${id_torre}`)
      .then(r => setVariaciones(r.data))
      .catch(e => console.error(e));
  };

  const handleAddVariacion = (e) => {
    e.preventDefault();
    axios.post(`${BASE}/variaciones`, { nombre: varName, id_torre })
      .then(() => { setVarName(''); setVarOpen(false); fetchVariaciones(); snack('success', 'Variación agregada correctamente'); })
      .catch(err => snack('error', err.response?.data?.error || 'Error al agregar variación'));
  };

  const handleEditVariacionSubmit = (e) => {
    e.preventDefault();
    axios.put(`${BASE}/variaciones/${varEditItem.id}`, { nombre: varEditItem.nombre })
      .then(() => { setVarEditOpen(false); setVarEditItem(null); fetchVariaciones(); snack('success', 'Variación actualizada correctamente'); })
      .catch(err => snack('error', err.response?.data?.error || 'Error al actualizar variación'));
  };

  const handleDeleteVariacion = (id) => {
    openConfirm('¿Eliminar esta variación?', () => {
      closeConfirm();
      axios.delete(`${BASE}/variaciones/${id}`)
        .then(() => { fetchVariaciones(); snack('success', 'Variación eliminada correctamente'); })
        .catch(err => snack('error', err.response?.data?.error || 'Error al eliminar variación'));
    });
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    axios.post(`${BASE}/addParking`, { num_parqueadero: numParqueadero, id_torre, id_variacion: addVariacionId || null })
      .then(() => {
        snack('success', 'Parqueadero agregado correctamente');
        getParkings(); getStats();
        setOpen(false); setNumParqueadero(''); setAddVariacionId('');
      })
      .catch(() => snack('error', 'Error al agregar el parqueadero'));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    axios.put(`${BASE}/updateParking/${selectedParking.id_parqueadero}`, {
      num_parqueadero: selectedParking.num_parqueadero,
      id_torre,
      id_variacion: selectedParking.id_variacion || null,
    })
      .then(() => { snack('success', 'Parqueadero actualizado correctamente'); getParkings(); setEditOpen(false); })
      .catch(() => snack('error', 'Error al actualizar el parqueadero'));
  };

  const handleDeleteParking = (id_parqueadero) => {
    openConfirm('¿Está seguro de eliminar este parqueadero?', () => {
      closeConfirm();
      axios.delete(`${BASE}/deleteParking/${id_parqueadero}`)
        .then(() => { snack('success', 'Parqueadero eliminado correctamente'); getParkings(); getStats(); })
        .catch(err => snack('error', err.response?.data?.error || 'Error al eliminar el parqueadero'));
    });
  };

  // Set of variacion ids that currently have parkings assigned
  const varConParkings = new Set(parkings.map(p => p.id_variacion).filter(Boolean));

  const term = searchTerm.toLowerCase();
  const filtered = parkings.filter(p =>
    p.num_parqueadero.toString().includes(term) ||
    p.estado.toLowerCase().includes(term) ||
    (p.variacion_nombre && p.variacion_nombre.toLowerCase().includes(term)) ||
    (p.placa && p.placa.toLowerCase().includes(term)) ||
    (p.usuario_nombre && p.usuario_nombre.toLowerCase().includes(term)) ||
    (p.num_apartamento && p.num_apartamento.toString().includes(term))
  );

  // Build groups: one per variacion, plus "Sin variación" at the end
  const groups = variaciones.map(v => ({
    key: v.id,
    label: v.nombre,
    isVariacion: true,
    items: filtered.filter(p => p.id_variacion === v.id),
  }));
  const sinVariacion = filtered.filter(p => !p.id_variacion);
  if (sinVariacion.length > 0 || variaciones.length === 0) {
    groups.push({ key: 'sin', label: 'Sin variación', isVariacion: false, items: sinVariacion });
  }

  const TableHeaders = () => (
    <thead className="bg-gradient-to-r from-purple-500 to-indigo-600">
      <tr>
        {['Número', 'Estado', 'Vehículo (Placa)', 'Propietario', 'Apartamento', 'Acciones'].map(h => (
          <th key={h} className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">{h}</th>
        ))}
      </tr>
    </thead>
  );

  const ParkingRow = ({ parking }) => (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">#{parking.num_parqueadero}</td>
      <td className="px-6 py-4 text-center">
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
          parking.estado === 'Disponible' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>{parking.estado}</span>
      </td>
      <td className="px-6 py-4 text-center text-sm text-gray-700">
        {parking.placa
          ? <div><div className="font-bold">{parking.placa}</div><div className="text-xs text-gray-500">{parking.marca} {parking.modelo}</div></div>
          : <span className="text-gray-400">-</span>}
      </td>
      <td className="px-6 py-4 text-center text-sm text-gray-900">
        {parking.usuario_nombre ? `${parking.usuario_nombre} ${parking.usuario_apellido}` : '-'}
      </td>
      <td className="px-6 py-4 text-center text-sm font-medium text-gray-900">{parking.num_apartamento || '-'}</td>
      <td className="px-6 py-4 text-center">
        <div className="flex justify-center gap-2">
          <button
            onClick={() => { setSelectedParking({ ...parking }); setEditOpen(true); }}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />Editar
          </button>
          {parking.estado === 'Disponible' && (
            <button
              onClick={() => handleDeleteParking(parking.id_parqueadero)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />Eliminar
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Back */}
          <button
            onClick={() => navigate('/adminParkings')}
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shadow-sm"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
            Volver
          </button>

          {/* Snackbar */}
          {openSnack && (
            <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg ${
              snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white`}>
              <div className="flex items-center gap-2">
                <span>{snackMessage}</span>
                <button onClick={() => setOpenSnack(false)}>
                  <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <FontAwesomeIcon icon={faLayerGroup} className="h-10 w-10 text-purple-600" />
            <h1 className="text-4xl font-bold text-gray-800">Parqueaderos de la Torre {id_torre}</h1>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Total Parqueaderos</p><p className="text-3xl font-bold text-gray-800">{stats.total}</p></div>
              <FontAwesomeIcon icon={faLayerGroup} className="h-12 w-12 text-purple-500" />
            </div>
            <div className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Ocupados</p><p className="text-3xl font-bold text-red-600">{stats.ocupados}</p></div>
              <FontAwesomeIcon icon={faTimesCircle} className="h-12 w-12 text-red-500" />
            </div>
            <div className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Disponibles</p><p className="text-3xl font-bold text-green-600">{stats.disponibles}</p></div>
              <FontAwesomeIcon icon={faCheckCircle} className="h-12 w-12 text-green-500" />
            </div>
          </div>

          {/* Variaciones panel */}
          <div className="bg-white rounded-2xl shadow-md p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTag} className="h-5 w-5 text-indigo-500" />
                <h2 className="text-lg font-bold text-gray-800">Variaciones</h2>
                <span className="text-sm text-gray-400">(Sótano 1, Sótano 2, Nivel A…)</span>
              </div>
              <button
                onClick={() => setVarOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} />Nueva variación
              </button>
            </div>
            {variaciones.length === 0 ? (
              <p className="text-sm text-gray-400">No hay variaciones. Agrega una para organizar los parqueaderos por sótano, nivel, bloque, etc.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {variaciones.map(v => {
                  const tieneAsignados = varConParkings.has(v.id);
                  return (
                    <span key={v.id} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-indigo-50 text-indigo-800 rounded-full text-sm font-medium border border-indigo-200">
                      {v.nombre}
                      <button
                        onClick={() => { setVarEditItem({ ...v }); setVarEditOpen(true); }}
                        className="text-indigo-400 hover:text-indigo-700 transition-colors p-0.5 rounded-full"
                        title="Editar variación"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} className="h-3.5 w-3.5" />
                      </button>
                      {!tieneAsignados && (
                        <button
                          onClick={() => handleDeleteVariacion(v.id)}
                          className="text-indigo-300 hover:text-red-500 transition-colors p-0.5 rounded-full"
                          title="Eliminar variación"
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Search + Add */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
            <input
              type="text"
              placeholder="Buscar por número, variación, estado, placa, propietario..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input-field w-full sm:max-w-md"
            />
            <button
              onClick={() => { setOpen(true); setNumParqueadero(''); setAddVariacionId(''); }}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5" />
              Agregar Parqueadero
            </button>
          </div>

          {/* Grouped tables */}
          <div className="space-y-8">
            {groups.map(group =>
              term && group.items.length === 0 ? null : (
                <div key={group.key}>
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon
                      icon={faTag}
                      className={`h-5 w-5 ${group.isVariacion ? 'text-indigo-500' : 'text-gray-400'}`}
                    />
                    <h3 className={`text-xl font-bold ${group.isVariacion ? 'text-indigo-700' : 'text-gray-500'}`}>
                      {group.label}
                    </h3>
                    <span className="text-sm text-gray-400">({group.items.length} parqueadero{group.items.length !== 1 ? 's' : ''})</span>
                  </div>
                  <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <TableHeaders />
                        <tbody className="bg-white divide-y divide-gray-200">
                          {group.items.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-10 text-gray-400">No hay parqueaderos en esta variación</td></tr>
                          ) : (
                            group.items.map(parking => <ParkingRow key={parking.id_parqueadero} parking={parking} />)
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Modal: nueva variación */}
          {varOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h2 className="text-xl font-bold text-gray-800">Nueva Variación</h2>
                </div>
                <form onSubmit={handleAddVariacion}>
                  <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Ej: Sótano 1, Nivel A, Bloque Norte..."
                      value={varName}
                      onChange={e => setVarName(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button type="button" onClick={() => { setVarOpen(false); setVarName(''); }} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                    <button type="submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold">Agregar</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: agregar parqueadero */}
          {open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h2 className="text-2xl font-bold text-gray-800">Agregar Parqueadero</h2>
                </div>
                <form onSubmit={handleAddSubmit}>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Número de Parqueadero *</label>
                      <input
                        type="number"
                        className="input-field"
                        value={numParqueadero}
                        onChange={e => setNumParqueadero(e.target.value)}
                        required
                        autoFocus
                        min="1"
                        placeholder="Ej: 101, 102..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Variación</label>
                      <select
                        className="input-field"
                        value={addVariacionId}
                        onChange={e => setAddVariacionId(e.target.value)}
                      >
                        <option value="">Sin variación</option>
                        {variaciones.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button type="button" onClick={() => setOpen(false)} className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                    <button type="submit" className="btn-primary">Agregar Parqueadero</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: editar parqueadero */}
          {editOpen && selectedParking && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h2 className="text-2xl font-bold text-gray-800">Editar Parqueadero</h2>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Número de Parqueadero *</label>
                      <input
                        type="number"
                        className="input-field"
                        value={selectedParking.num_parqueadero}
                        onChange={e => setSelectedParking({ ...selectedParking, num_parqueadero: e.target.value })}
                        required
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Variación</label>
                      <select
                        className="input-field"
                        value={selectedParking.id_variacion || ''}
                        onChange={e => setSelectedParking({ ...selectedParking, id_variacion: e.target.value || null })}
                      >
                        <option value="">Sin variación</option>
                        {variaciones.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button type="button" onClick={() => setEditOpen(false)} className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                    <button type="submit" className="btn-primary">Guardar Cambios</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: editar variación */}
          {varEditOpen && varEditItem && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-800">Editar Variación</h2>
                  <button onClick={() => setVarEditOpen(false)} className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                  </button>
                </div>
                <form onSubmit={handleEditVariacionSubmit}>
                  <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                    <input
                      type="text"
                      className="input-field"
                      value={varEditItem.nombre}
                      onChange={e => setVarEditItem({ ...varEditItem, nombre: e.target.value })}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button type="button" onClick={() => setVarEditOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                    <button type="submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold">Guardar</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Confirmation dialog */}
          {confirmDialog.open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faTrash} className="h-5 w-5 text-red-600" />
                    </div>
                    <p className="text-gray-800 font-medium">{confirmDialog.message}</p>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">Esta acción no se puede deshacer.</p>
                  <div className="flex justify-end gap-3">
                    <button onClick={closeConfirm} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                    <button onClick={confirmDialog.onConfirm} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold">Eliminar</button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default ControlParkings;

